import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowDown,
  ArrowUp,
  Columns3,
  Loader2,
  History,
  Search,
  X,
} from "lucide-react";
import type { WorkflowRunColumn } from "@proclaw/shared";
import { Button } from "@/components/ui/button";
import { useTenant } from "@/context/tenant-context";
import {
  workflowsApi,
  workflowRunsApi,
  type WorkflowRunRow,
} from "@/api/workflows";
import { queryKeys } from "@/lib/query-keys";
import { RunStatusBadge } from "@/components/agent/agent-workflows-tab";
import { RunColumnsDialog } from "@/components/workflows/run-columns-dialog";
import { withWorkspaceContext } from "@/lib/workspace-navigation";
import { cn } from "@/lib/utils";
import { WorkflowTabs } from "@/components/workflows/workflow-tabs";

const STATUS_FILTERS = [
  "all",
  "running",
  "awaiting",
  "completed",
  "failed",
  "cancelled",
] as const;

const PAGE_SIZE = 50;

/** Fixed columns that can be sorted on, keyed by the server's sortBy values. */
type SortKey = string;

/**
 * Per-workflow runs list. Reachable from the canvas page's "Runs" button
 * and from the agent Workflows tab. Filterable by status, searchable across
 * the workflow's custom columns, sortable, and paged.
 *
 * Search and sort are server-side: filtering the page the client happens to
 * hold would miss exactly the run you're hunting for once a workflow has more
 * runs than fit in one page.
 */
export function WorkflowRunsList() {
  const { workflowId } = useParams<{ workflowId: string }>();
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] =
    useState<(typeof STATUS_FILTERS)[number]>("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<SortKey>("startedAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [pageCount, setPageCount] = useState(1);
  const [showColumns, setShowColumns] = useState(false);

  // Debounce so typing a case number doesn't fire a query per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Any change to what's being asked for collapses back to a single page —
  // otherwise a search would inherit the page count from the previous view
  // and over-fetch.
  useEffect(() => {
    setPageCount(1);
  }, [search, statusFilter, sortBy, sortDir]);

  const { data: workflow } = useQuery({
    queryKey: queryKeys.workflows.detail(workflowId ?? ""),
    queryFn: () => workflowsApi.get(workflowId!),
    enabled: !!workflowId,
  });

  const limit = PAGE_SIZE * pageCount;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      ...queryKeys.workflowRuns.byWorkflow(workflowId ?? ""),
      statusFilter,
      search,
      sortBy,
      sortDir,
      limit,
    ],
    queryFn: () =>
      workflowRunsApi.listForWorkflow(workflowId!, {
        limit,
        status: statusFilter === "all" ? undefined : statusFilter,
        search: search || undefined,
        sortBy,
        sortDir,
      }),
    enabled: !!workflowId,
    // Growing the page count refetches with a larger limit; holding the
    // previous rows keeps the table from blanking out mid-scroll.
    placeholderData: keepPreviousData,
    // Only poll the live view. Auto-refreshing while someone is reading
    // search results would reorder the table under them.
    refetchInterval: search || pageCount > 1 ? false : 5_000,
  });

  const runs = data?.runs ?? [];
  const customColumns: WorkflowRunColumn[] = data?.columns ?? [];

  const toggleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDir("desc");
    }
  };

  if (!workflowId) return null;

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      {/* ── Top bar ───────────────────────────────────────────────── */}
      <div className="flex shrink-0 items-center gap-3 border-b border-border bg-surface px-4 py-2">
        <button
          onClick={() =>
            navigate(
              withWorkspaceContext(
                `/tenants/${tenantId}/workflows/${workflowId}`,
                searchParams,
              ),
            )
          }
          className="flex h-7 w-7 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-surface-hover hover:text-text-primary"
          aria-label="Back to canvas"
        >
          <ArrowLeft size={14} />
        </button>
        <div className="flex flex-1 items-center gap-2 text-sm">
          <History size={14} className="text-text-muted" />
          <span className="font-semibold text-text-primary">
            {workflow?.name ?? "Workflow"}
          </span>
          <span className="text-text-muted">/ runs</span>
        </div>
        {tenantId && workflowId && (
          <WorkflowTabs tenantId={tenantId} workflowId={workflowId} active="runs" />
        )}

        {/* Search */}
        <div className="relative">
          <Search
            size={12}
            className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-text-muted"
          />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={
              customColumns.length > 0
                ? `Search ${customColumns.map((c) => c.label).join(", ")}`
                : "Add a column to search"
            }
            disabled={customColumns.length === 0}
            className="w-56 rounded-md border border-border bg-surface py-1 pl-7 pr-6 text-xs text-text-secondary placeholder:text-text-muted disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Search runs"
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput("")}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
              aria-label="Clear search"
            >
              <X size={11} />
            </button>
          )}
        </div>

        <select
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(e.target.value as (typeof STATUS_FILTERS)[number])
          }
          className="rounded-md border border-border bg-surface px-2 py-1 text-xs text-text-secondary"
          aria-label="Filter by status"
        >
          {STATUS_FILTERS.map((s) => (
            <option key={s} value={s}>
              {s === "all" ? "All statuses" : s}
            </option>
          ))}
        </select>

        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowColumns(true)}
          className="gap-1.5"
        >
          <Columns3 size={12} />
          Columns
        </Button>
      </div>

      {/* ── Body ──────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <Loader2 size={18} className="animate-spin text-text-muted" />
        ) : runs.length === 0 ? (
          <EmptyState statusFilter={statusFilter} search={search} />
        ) : (
          <>
            <div className="overflow-x-auto rounded-md border border-border bg-surface">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                    <th className="px-3 py-2 text-left">Run</th>
                    <SortableHeader
                      label="Status"
                      sortKey="status"
                      activeKey={sortBy}
                      dir={sortDir}
                      onSort={toggleSort}
                    />
                    <th className="px-3 py-2 text-left">Trigger</th>
                    <th className="px-3 py-2 text-left">Agent</th>
                    {customColumns.map((c) => (
                      <SortableHeader
                        key={c.key}
                        label={c.label}
                        sortKey={c.key}
                        activeKey={sortBy}
                        dir={sortDir}
                        onSort={toggleSort}
                      />
                    ))}
                    <SortableHeader
                      label="Started"
                      sortKey="startedAt"
                      activeKey={sortBy}
                      dir={sortDir}
                      onSort={toggleSort}
                    />
                    <SortableHeader
                      label="Duration"
                      sortKey="duration"
                      activeKey={sortBy}
                      dir={sortDir}
                      onSort={toggleSort}
                    />
                    <th className="px-3 py-2 text-left">Outcome</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => (
                    <RunRow
                      key={r.id}
                      run={r}
                      customColumns={customColumns}
                      onClick={() =>
                        navigate(
                          withWorkspaceContext(
                            `/tenants/${tenantId}/workflows/${workflowId}/runs/${r.id}`,
                            searchParams,
                          ),
                        )
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-3 flex items-center justify-center gap-3">
              {data?.hasMore ? (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={isFetching}
                  onClick={() => setPageCount((p) => p + 1)}
                  className="gap-1.5"
                >
                  {isFetching && (
                    <Loader2 size={12} className="animate-spin" />
                  )}
                  Load more
                </Button>
              ) : (
                <p className="text-[11px] text-text-muted">
                  {runs.length} run{runs.length === 1 ? "" : "s"}
                  {search ? " matching" : ""} — end of list
                </p>
              )}
            </div>
          </>
        )}
      </div>

      {showColumns && (
        <RunColumnsDialog
          workflowId={workflowId}
          initialColumns={customColumns}
          onClose={() => setShowColumns(false)}
        />
      )}
    </div>
  );
}

function EmptyState({
  statusFilter,
  search,
}: {
  statusFilter: string;
  search: string;
}) {
  const heading = search
    ? "No runs match that search"
    : statusFilter === "all"
      ? "No runs yet"
      : `No ${statusFilter} runs`;
  const detail = search
    ? "Search looks at your custom column values only. Check the column is configured and has been backfilled."
    : statusFilter === "all"
      ? "Use the Run button on the canvas to kick one off, or trigger it on a schedule or webhook."
      : "Try a different status filter.";
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-surface/50 py-14 text-center">
      <History size={28} className="mb-3 text-text-muted" />
      <p className="text-sm font-medium text-text-primary">{heading}</p>
      <p className="mt-1 max-w-sm text-xs text-text-muted">{detail}</p>
    </div>
  );
}

function SortableHeader({
  label,
  sortKey,
  activeKey,
  dir,
  onSort,
}: {
  label: string;
  sortKey: string;
  activeKey: string;
  dir: "asc" | "desc";
  onSort: (key: string) => void;
}) {
  const active = activeKey === sortKey;
  return (
    <th className="px-3 py-2 text-left">
      <button
        onClick={() => onSort(sortKey)}
        className={cn(
          "flex items-center gap-1 uppercase tracking-wide transition-colors hover:text-text-primary",
          active && "text-text-primary",
        )}
      >
        {label}
        {active &&
          (dir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />)}
      </button>
    </th>
  );
}

function RunRow({
  run,
  customColumns,
  onClick,
}: {
  run: WorkflowRunRow;
  customColumns: WorkflowRunColumn[];
  onClick: () => void;
}) {
  const duration = formatDuration(run.startedAt, run.completedAt);
  const outcome =
    run.status === "failed"
      ? run.error?.message ?? "failed"
      : run.status === "awaiting"
        ? `awaiting ${run.awaitingType ?? "event"}`
        : run.status === "running" && run.retryAt
          ? "retry pending"
          : run.status === "completed"
            ? "✓"
            : "—";
  return (
    <tr
      className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-surface-hover"
      onClick={onClick}
    >
      <td className="px-3 py-2 font-mono text-[11px] text-text-secondary">
        {run.id.slice(0, 8)}
      </td>
      <td className="px-3 py-2">
        <RunStatusBadge status={run.status} />
      </td>
      <td className="px-3 py-2">
        {run.triggerSource ? (
          <span className="rounded bg-surface-hover px-1.5 py-0.5 text-[11px] text-text-secondary">
            {run.triggerSource}
          </span>
        ) : (
          <span className="text-xs text-text-muted">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-xs text-text-secondary">
        {run.assignedAgentName ?? "—"}
      </td>
      {customColumns.map((c) => {
        const value = run.columnValues?.[c.key];
        return (
          <td
            key={c.key}
            className="max-w-[12rem] truncate px-3 py-2 text-xs text-text-secondary"
            title={value || undefined}
          >
            {value || <span className="text-text-muted">—</span>}
          </td>
        );
      })}
      <td className="px-3 py-2 text-xs text-text-muted">
        {new Date(run.startedAt).toLocaleString()}
      </td>
      <td className="px-3 py-2 text-xs text-text-muted">{duration}</td>
      <td
        className="max-w-[16rem] truncate px-3 py-2 text-xs text-text-muted"
        title={typeof outcome === "string" ? outcome : undefined}
      >
        {outcome}
      </td>
    </tr>
  );
}

function formatDuration(startedAt: string, completedAt: string | null): string {
  const end = completedAt ? new Date(completedAt).getTime() : Date.now();
  const ms = end - new Date(startedAt).getTime();
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  if (ms < 3_600_000) {
    const mins = Math.floor(ms / 60_000);
    const secs = Math.floor((ms % 60_000) / 1000);
    return `${mins}m ${secs}s`;
  }
  const hrs = Math.floor(ms / 3_600_000);
  const mins = Math.floor((ms % 3_600_000) / 60_000);
  return `${hrs}h ${mins}m`;
}
